import { CornerUpLeft, MessageCircle, Send, ShieldCheck } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type {
  ChatMessage,
  Locale,
  PublicEvent,
  PublicTenant,
} from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { ApiError, apiFetch } from "../lib/api";
import { authClient } from "../lib/auth-client";
import { chatIdentity } from "../lib/chat-identity";
import { linkify } from "../lib/linkify";
import type { ChatStreamEvent } from "../hooks/useTimeline";

const nicknameKey = "vzlive:chat-name";

const CHAT_BASE = "/api/public/events";

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: (code: string) => void;
      retry?: "auto" | "never";
      "retry-interval"?: number;
      "refresh-expired"?: "auto" | "never";
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileLoader: Promise<TurnstileApi> | null = null;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  turnstileLoader ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile unavailable"));
    };
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.append(script);
  });
  return turnstileLoader;
};

export function LiveChat({
  tenant,
  event,
  locale,
  onChatEvent,
}: {
  tenant: PublicTenant;
  event: PublicEvent;
  locale: Locale;
  onChatEvent: (listener: (event: ChatStreamEvent) => void) => () => void;
}) {
  const enabled = tenant.chatEnabled && event.chatEnabled;
  const readonly = event.status === "archived";
  const paused = event.chatPaused || tenant.isPaused;

  const sessionState = authClient.useSession();
  const session = sessionState.data;
  // Eigene Nachricht → rechtsbündig wie im Messenger (vgl. natives Widget).
  const ownUserId = session?.user?.id ?? null;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  // Pre-Clearance: Das Widget wird schon beim Fokus auf das Eingabefeld
  // gelöst, nicht erst nach dem ersten Fehlversuch (403).
  const [preClear, setPreClear] = useState(false);
  // Einmal verifiziert bleibt das Widget weg – der Server fordert Turnstile
  // ohnehin nur bei der ersten Nachricht (priorMessages == 0). Sonst poppt
  // das Widget bei jedem Fokus wieder auf.
  const [captchaDone, setCaptchaDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [captchaRender, setCaptchaRender] = useState(0);
  const [needsTurnstile, setNeedsTurnstile] = useState(false);
  const [nickname, setNickname] = useState(() => {
    try {
      return localStorage.getItem(nicknameKey) ?? "";
    } catch {
      return "";
    }
  });
  // Namen nur einmal abfragen: sobald er bekannt ist, reicht ein kleiner
  // "als Maik · ändern"-Chip über dem Eingabefeld.
  const [nameOpen, setNameOpen] = useState(() => !nickname.trim());
  const [notice, setNotice] = useState("");
  // Antwort-Bezug: wird beim Absenden als replyToId mitgereist und danach
  // wieder verworfen – wie im Messenger.
  const [replyTarget, setReplyTarget] = useState<ChatMessage | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [blockedReason, setBlockedReason] = useState<
    null | "muted" | "banned" | "rejected" | "error"
  >(null);
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Fetch initial history when the chat first becomes usable.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void loadChat();
    async function loadChat() {
      try {
        const chat = await apiFetch<{
          messages: ChatMessage[];
          chatPaused: boolean;
          captcha: { required: boolean; siteKey: string | null };
        }>(`${CHAT_BASE}/${encodeURIComponent(event.slug)}/chat`);
        if (cancelled) return;
        setMessages(chat.messages);
        setSiteKey(chat.captcha.siteKey);
        setLoaded(true);
      } catch {
        if (!cancelled) {
          setLoaded(true);
          setBlockedReason("error");
        }
      }
    }
    return () => {
      cancelled = true;
    };
  }, [enabled, event.slug]);

  useEffect(() => {
    const unsubscribe = onChatEvent((change) => {
      if (change.type === "chat.created") {
        setMessages((current) => {
          if (current.some((entry) => entry.id === change.message.id)) {
            return current;
          }
          // Chronologisch einsortieren (SSE-Kreuzfahrten möglich):
          // die neueste Nachricht steht immer unten.
          const key = (entry: ChatMessage) =>
            `${entry.createdAt ?? ""}\u0000${entry.id ?? ""}`;
          const firstAfter = current.findIndex(
            (entry) => key(entry) > key(change.message),
          );
          return firstAfter < 0
            ? [...current, change.message]
            : current.toSpliced(firstAfter, 0, change.message);
        });
      } else {
        // Eine entfernte Nachricht verschwindet für alle gleich, weil der
        // Redakteur sie moderiert hat.
        setMessages((current) =>
          current.filter((entry) => entry.id !== change.messageId),
        );
      }
    });
    return unsubscribe;
  }, [onChatEvent]);

  // Sticky Scroll wie im Widget: nur der Liste nachziehen, wer schon unten
  // war – wer hochscrollt, wird nicht beim Lesen weggezogen.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const stuck = list.scrollHeight - list.scrollTop - list.clientHeight < 60;
    if (stuck) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  const ensureAnonymousSession = useCallback(async () => {
    if (!nickname.trim()) return false;
    // Der Nickname lebt als Chat-authorName; der anonyme Better-Auth-User
    // erzeugt seinen Platzhalternamen nur serverseitig.
    const { data } = await authClient.signIn.anonymous();
    return Boolean(data?.user);
  }, []);

  const postMessageRequest = useCallback(
    async (content: string, token: string, replyToId?: string) => {
      return await apiFetch<{ message: ChatMessage }>(
        `${CHAT_BASE}/${encodeURIComponent(event.slug)}/chat/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            content,
            replyToId: replyToId || undefined,
            nickname: nickname.trim(),
            turnstileToken: token || undefined,
            clientMutationId: crypto.randomUUID(),
          }),
        },
      );
    },
    [event.slug, nickname],
  );

  const send = useCallback(
    async (content: string) => {
      if (sending) return false;
      setSending(true);
      try {
        const response = await postMessageRequest(
          content,
          turnstileToken,
          replyTarget?.id,
        );
        setMessages((current) => [...current, response.message]);
        setBlockedReason(null);
        setCaptchaDone(true);
        setNotice("");
        setPreClear(false);
        setNeedsTurnstile(false);
        setTurnstileToken("");
        setReplyTarget(null);
        return true;
      } catch (error) {
        if (error instanceof ApiError) {
          if (error.status === 403 && error.code === "CHAT_CAPTCHA_REQUIRED") {
            setNeedsTurnstile(true);
            setPreClear(true);
            setNotice(t(locale, "chatCaptchaPending"));
            return false;
          }
          if (error.code === "CHAT_CAPTCHA_FAILED") {
            setNeedsTurnstile(true);
            setPreClear(true);
            setTurnstileToken("");
            window.turnstile?.reset();
            setNotice(t(locale, "chatCaptchaPending"));
            return false;
          }
          if (error.code === "CHAT_SLOW_MODE") {
            const raw = (error.details as { waitSeconds?: number })
              ?.waitSeconds;
            const waitSeconds = Math.max(0, Math.ceil(Number(raw) || 0));
            setNotice(
              t(locale, "chatSlowMode").replace(
                "{seconds}",
                String(waitSeconds),
              ),
            );
            return false;
          }
          if (error.code === "CHAT_TIMEOUT" || error.code === "CHAT_BANNED") {
            setBlockedReason(
              error.code === "CHAT_TIMEOUT" ? "muted" : "banned",
            );
            return false;
          }
          if (error.code === "CHAT_MESSAGE_BLOCKED") {
            setBlockedReason("rejected");
            return false;
          }
          if (error.code === "CHAT_DISABLED") {
            setBlockedReason(null);
            setNotice(t(locale, "chatReadonlyArchived"));
            return false;
          }
        }
        if (error instanceof ApiError && error.status === 401) {
          const joined = await ensureAnonymousSession();
          if (joined) {
            try {
              const response = await postMessageRequest(
                content,
                turnstileToken,
                replyTarget?.id,
              );
              setMessages((current) => [...current, response.message]);
              setBlockedReason(null);
              setCaptchaDone(true);
              setNotice("");
              setPreClear(false);
              setNeedsTurnstile(false);
              setTurnstileToken("");
              setReplyTarget(null);
              return true;
            } catch {
              setBlockedReason("error");
              return false;
            }
          }
          setNotice(t(locale, "chatNameError"));
          return false;
        }
        setBlockedReason("error");
        return false;
      } finally {
        setSending(false);
      }
    },
    [
      ensureAnonymousSession,
      event.slug,
      locale,
      nickname,
      postMessageRequest,
      replyTarget,
      sending,
      turnstileToken,
    ],
  );

  const handleSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
    (submission) => {
      submission.preventDefault();
      if (sending || readonly || blockedReason === "rejected") return;
      const form = submission.currentTarget;
      const content = String(new FormData(form).get("content") ?? "").trim();
      if (!content) return;
      if (!nickname.trim()) {
        setNameOpen(true);
        setNotice(t(locale, "chatNameError"));
        (form.querySelector('[name="nickname"]') as HTMLInputElement)?.focus();
        return;
      }
      try {
        localStorage.setItem(nicknameKey, nickname.trim());
      } catch {
        // Speicher verweigert: Name lebt dann nur pro Nachricht weiter.
      }
      setBlockedReason(null);
      setNotice(needsTurnstile ? t(locale, "chatCaptchaPending") : "");
      // Bei fehlgeschlagenem Senden bleibt der Text im Feld – keine
      // verlorene Nachricht, auch wenn Captchas lang dauern.
      void send(content).then((ok) => {
        if (ok) setNameOpen(false);
        const input = form.querySelector(
          '[name="content"]',
        ) as HTMLInputElement;
        if (ok) {
          form.reset();
        } else if (input && !input.value.trim()) {
          input.value = content;
        }
      });
    },
    [blockedReason, event.id, locale, needsTurnstile, nickname, send, sending],
  );

  // Captcha-Bereich erscheint nur bis zur ersten Verifizierung: beim
  // Fokus (Pre-Clearance) oder wenn der Server ein Token verlangt.
  const showCaptcha = siteKey && preClear && !turnstileToken;

  // Turnstile-Widget mit Auto-Retry: "refresh-expired" erneuert ablaufende
  // Tokens selbst, der hyphenate-Callback-Name ist Pflicht (camelCase wird
  // von Cloudflare ignoriert – das war die Ursache für den eingefrorenen
  // Bestätigungs-Chip). Im error-callback KEIN reset(): Der wiederholte
  // Widget-Load bei Fehler (z. B. 400020) sonst Endlos-Loop – statt dessen
  // zeigt der Nutzer einen manuellen "Erneut versuchen"-Button.
  useEffect(() => {
    if (!showCaptcha || !siteKey) return;
    let widgetId: string | null = null;
    let cancelled = false;
    void loadTurnstile().then((turnstile) => {
      if (cancelled) return;
      const container = document.getElementById("vzlive-turnstile-slot");
      if (!container) return;
      container.replaceChildren();
      widgetId = turnstile.render(container, {
        sitekey: siteKey,
        retry: "auto",
        "refresh-expired": "auto",
        callback: (token) => {
          setCaptchaError(null);
          setTurnstileToken(token);
          setNotice("");
        },
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": (code) => {
          // Kein reset() hier – der Nutzer steuert den Retry manuell.
          console.warn(
            `[vz.live] Turnstile-Fehler ${code ?? "unbekannt"} – Sitekey/Domain im Turnstile-Dashboard prüfen (Domain-Whitelist: ${window.location.hostname}).`,
          );
          setCaptchaError(String(code ?? "error"));
          setTurnstileToken("");
        },
      });
    });
    return () => {
      cancelled = true;
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, [showCaptcha, captchaRender, siteKey]);

  if (!enabled) return null;

  return (
    <section className="live-chat" aria-label={t(locale, "chatTitle")}>
      <header className="live-chat__heading">
        <MessageCircle size={17} aria-hidden="true" />
        <h2>{t(locale, "chatTitle")}</h2>
      </header>

      {paused ? (
        <div className="live-chat__paused">
          <strong>{t(locale, "chatPausedTitle")}</strong>
          <p>{t(locale, "chatPausedDescription")}</p>
        </div>
      ) : (
        <>
          <div className="live-chat__messages" ref={listRef}>
            {messages.map((message) => {
              // Ist-Nachrichten links, eigene rechts (Messenger-Look),
              // Redaktion mit Badge; die eigene Identität zeigt die
              // Ini-Grundform wie überall.
              const identity = chatIdentity(
                message.authorName,
                message.authorUserId,
              );
              const own =
                message.authorUserId && ownUserId
                  ? message.authorUserId === ownUserId
                  : false;
              return (
                <article
                  key={message.id}
                  className={own ? "live-chat__msg--own" : undefined}
                >
                  <span
                    className="live-chat__avatar"
                    style={{ background: identity.color }}
                    aria-hidden="true"
                  >
                    {identity.initials}
                  </span>
                  <div>
                    <header>
                      <strong>{message.authorName}</strong>
                      <time dateTime={message.createdAt}>
                        {new Intl.DateTimeFormat(locale, {
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(new Date(message.createdAt))}
                      </time>
                      {message.editor && (
                        <span className="live-chat__badge">
                          {t(locale, "chatEditorBadge")}
                        </span>
                      )}
                    </header>
                    {message.replyTo && (
                      <blockquote className="live-chat__replyquote">
                        <strong>{message.replyTo.authorName}</strong>
                        <span>{message.replyTo.content}</span>
                      </blockquote>
                    )}
                    <p>{linkify(message.content)}</p>
                    {!readonly && !paused && (
                      <button
                        type="button"
                        className="live-chat__reply"
                        aria-label={t(locale, "chatReply")}
                        title={t(locale, "chatReply")}
                        onClick={() => {
                          setReplyTarget(message);
                          inputRef.current?.focus();
                        }}
                      >
                        <CornerUpLeft size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
            {!messages.length && loaded && (
              <p className="live-chat__empty">{t(locale, "chatEmpty")}</p>
            )}
          </div>

          {readonly ? (
            <p className="live-chat__notice">
              {t(locale, "chatReadonlyArchived")}
            </p>
          ) : (
            <form className="live-chat__form" onSubmit={handleSubmit}>
              {nameOpen ? (
                <input
                  name="nickname"
                  aria-label={t(locale, "chatNameLabel")}
                  placeholder={t(locale, "chatNameLabel")}
                  maxLength={40}
                  defaultValue={nickname}
                  onChange={(change) => setNickname(change.target.value)}
                  onFocus={() => siteKey && !captchaDone && setPreClear(true)}
                  required
                />
              ) : (
                <p className="live-chat__who">
                  <span>
                    {t(locale, "chatAsName").replace("{name}", nickname)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setNameOpen(true)}
                    aria-label={t(locale, "chatNameLabel")}
                  >
                    {t(locale, "chatNameChange")}
                  </button>
                </p>
              )}
              <div className="live-chat__compose">
                {replyTarget && (
                  <p className="live-chat__replyline">
                    <span>
                      {t(locale, "chatReplyTo").replace(
                        "{name}",
                        replyTarget.authorName,
                      )}
                    </span>
                    <button
                      type="button"
                      aria-label={t(locale, "chatReplyCancel")}
                      onClick={() => setReplyTarget(null)}
                    >
                      ×
                    </button>
                  </p>
                )}
                <input
                  ref={inputRef}
                  name="content"
                  aria-label={t(locale, "chatPlaceholder")}
                  placeholder={t(locale, "chatPlaceholder")}
                  maxLength={500}
                  autoComplete="off"
                  disabled={blockedReason === "banned"}
                  onFocus={() => siteKey && !captchaDone && setPreClear(true)}
                />
                <button
                  type="submit"
                  className="live-chat__send"
                  disabled={sending || (needsTurnstile && !turnstileToken)}
                  title={
                    needsTurnstile && !turnstileToken
                      ? t(locale, "chatCaptchaPending")
                      : undefined
                  }
                >
                  <Send size={16} aria-hidden="true" />
                  <span>{t(locale, "chatSend")}</span>
                </button>
              </div>
              {showCaptcha && (
                <div className="live-chat__captcha">
                  <span>{t(locale, "chatCaptchaLabel")}</span>
                  {captchaError ? (
                    <div className="live-chat__captcha-error">
                      <p className="live-chat__notice" role="alert">
                        {t(locale, "chatCaptchaFailed").replace(
                          "{code}",
                          captchaError,
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setTurnstileToken("");
                          setCaptchaError(null);
                          setCaptchaRender((current) => current + 1);
                        }}
                      >
                        {t(locale, "chatCaptchaRetry")}
                      </button>
                    </div>
                  ) : (
                    <div id="vzlive-turnstile-slot" />
                  )}
                  <ShieldCheck size={16} aria-hidden="true" />
                </div>
              )}
              {notice && (
                <p className="live-chat__notice" role="status">
                  {notice}
                </p>
              )}
              {blockedReason && (
                <p className="live-chat__block" role="alert">
                  {
                    {
                      muted: t(locale, "chatMuted"),
                      banned: t(locale, "chatBanned"),
                      rejected: t(locale, "chatRejected"),
                      error: t(locale, "chatError"),
                    }[blockedReason]
                  }
                </p>
              )}
            </form>
          )}
        </>
      )}
    </section>
  );
}
